/**
 * Prefetch on intent — ARCHITECTURE.md §4.8c, ROADMAP 3.16.
 *
 * "Tab hover, dropdown mouse-down, scroll velocity — all precede the commit
 * by 200–500 ms. Most switches complete before the click lands."
 *
 * A chain switch costs an expiry list, a chain fetch and a snapshot. Starting
 * them on `mouseenter` rather than on `click` buys most of that time for
 * free, because a person's hand is already moving before they decide.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT MAKES THIS SAFE TO DO ON A HOVER
 *
 * Every request here is a READ, and a read that is thrown away costs a
 * cached response and nothing else. Nothing is subscribed, nothing is
 * mutated, and no broker call is made — the snapshot DOES reach the broker,
 * so it is deliberately NOT part of the hover path. Hovering across five
 * index tabs must not fire five rate-limited quote batches ([B10]).
 *
 * So: hover warms the catalogue (our own database), and the snapshot waits
 * for a real commit.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { api, qs } from '@/lib/api'
import { TIER1_INDICES, WARM_CHAIN_EXPIRIES, istDateOf, type ExpiryListResponse, type InstrumentSearchResponse } from '@apna/shared'

/** Responses already fetched, so a second hover is free. */
const expiryCache = new Map<string, Promise<ExpiryListResponse>>()
const chainCache = new Map<string, Promise<InstrumentSearchResponse>>()
const expiryValues = new Map<string, ExpiryListResponse>()
const chainValues = new Map<string, InstrumentSearchResponse>()
const listeners = new Set<() => void>()
let revision = 0
let sessionDay = istDateOf(Date.now())
const changed = () => { revision += 1; for (const listener of listeners) listener() }
export const subscribeCatalogue = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}
export const catalogueRevision = (): number => revision
export const cachedExpiries = (underlying: string): ExpiryListResponse | undefined => expiryValues.get(underlying)
export const cachedChain = (underlying: string, expiry: string): InstrumentSearchResponse | undefined => chainValues.get(`${underlying}|${expiry}`)

/**
 * Bounded, because a user can hover a lot of tabs in a session and a chain
 * response is ~236 rows. Oldest out first; these are caches, not state.
 */
const MAX_CACHED = 32
const primary = new Set<string>(TIER1_INDICES)

function protectedKey(key: string): boolean {
  const [underlying, expiry] = key.split('|')
  if (!underlying || !primary.has(underlying)) return false
  return expiry === undefined || Boolean(expiryValues.get(underlying)?.expiries.slice(0, WARM_CHAIN_EXPIRIES).includes(expiry))
}

function remember<T>(cache: Map<string, Promise<T>>, values: Map<string, T>, key: string, make: () => Promise<T>): Promise<T> {
  const day = istDateOf(Date.now())
  if (day !== sessionDay) {
    sessionDay = day
    expiryCache.clear(); chainCache.clear(); expiryValues.clear(); chainValues.clear()
  }
  const hit = cache.get(key)
  if (hit) {
    cache.delete(key); cache.set(key, hit)
    return hit
  }
  const p = make().then((value) => {
    // A response evicted or superseded while in flight must not resurrect itself.
    if (cache.get(key) === p) { values.set(key, value); changed() }
    return value
  }).catch((err: unknown) => {
    // A failed prefetch must not be remembered as a failure: the real click
    // should get a fresh attempt rather than inherit a rejected promise.
    if (cache.get(key) === p) cache.delete(key)
    throw err
  })
  cache.set(key, p)
  if (cache.size > MAX_CACHED) {
    const oldest = [...cache.keys()].find((candidate) => !protectedKey(candidate))
    if (oldest !== undefined) { cache.delete(oldest); values.delete(oldest) }
  }
  return p
}

export function prefetchExpiries(underlying: string): Promise<ExpiryListResponse> {
  return remember(expiryCache, expiryValues, underlying, () =>
    api.get<ExpiryListResponse>(`/instruments/expiries/${underlying}`),
  )
}

export function prefetchChain(underlying: string, expiry: string): Promise<InstrumentSearchResponse> {
  return remember(chainCache, chainValues, `${underlying}|${expiry}`, () =>
    api.get<InstrumentSearchResponse>(`/instruments/chain${qs({ underlying, expiry })}`),
  )
}

/**
 * Warm everything a switch to this underlying will need.
 *
 * Expiries first, then the nearest expiry's chain — which is the one the
 * screen selects on arrival, so it is the only one worth guessing at.
 *
 * Swallows errors on purpose: a prefetch that fails has cost nothing and
 * must not surface to a user who has not clicked anything.
 */
export function prefetchUnderlying(underlying: string): void {
  void prefetchExpiries(underlying)
    .then((res) => {
      for (const expiry of res.expiries.slice(0, WARM_CHAIN_EXPIRIES)) {
        void prefetchChain(underlying, expiry).catch(() => undefined)
      }
    })
    .catch(() => undefined)
}

/** Hover handlers for a tab. Spread onto the button. */
export const onIntent = (fn: () => void) => ({
  onMouseEnter: fn,
  // Touch has no hover; `pointerdown` still precedes the click by enough to
  // matter on a phone or a trackpad tap.
  onPointerDown: fn,
})
